import { useEffect, useCallback, useRef } from "react";
import { useBlocker } from "react-router-dom";

interface UseNavigationBlockerOptions {
  when?: boolean;
  message?: string;
}

// Global flag to bypass blocker during sign-out
let bypassBlocker = false;

export function setBypassBlocker(bypass: boolean) {
  bypassBlocker = bypass;
}

export function useNavigationBlocker(options: UseNavigationBlockerOptions = {}) {
  const { when = true, message = "Are you sure you want to leave the dashboard?" } = options;
  const hasConfirmedRef = useRef(false);

  // Use the blocker with immediate confirmation via window.confirm
  const blocker = useBlocker(
    useCallback(
      ({ currentLocation, nextLocation }) => {
        // Skip blocking entirely if bypass flag is set (e.g., during sign-out)
        if (bypassBlocker) return false;
        if (!when) return false;
        if (currentLocation.pathname === nextLocation.pathname) return false;
        
        // If user already confirmed, allow navigation
        if (hasConfirmedRef.current) {
          hasConfirmedRef.current = false;
          return false;
        }
        
        // Use native confirm dialog - returns true if user confirms, false if cancels
        const shouldLeave = window.confirm(message);
        
        if (shouldLeave) {
          hasConfirmedRef.current = true;
          // Return false to allow navigation
          return false;
        }
        
        // Return true to BLOCK navigation (user clicked Cancel)
        return true;
      },
      [when, message]
    )
  );

  // If blocker is in blocked state, proceed with navigation
  useEffect(() => {
    if (blocker.state === "blocked" && hasConfirmedRef.current) {
      blocker.proceed?.();
      hasConfirmedRef.current = false;
    }
  }, [blocker]);

  // These are no longer used but kept for API compatibility
  return {
    showDialog: false,
    confirmNavigation: () => {},
    cancelNavigation: () => {},
    message,
  };
};
